import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";

const KEY = "mp_trial_notice";

// Gentle heads-up shown in the final 2 days of the free trial so features locking isn't a surprise.
export function TrialEndingNotice() {
  const { t } = useI18n();
  const { sub } = useSubscription();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [dismissed, setDismissed] = useState(localStorage.getItem(KEY) === today);

  const ending = sub?.status === "trialing" && (sub?.trial_days_left ?? 99) <= 2;
  if (!ending || dismissed) return null;

  const days = sub.trial_days_left;
  const daysLabel = `${days} ${days === 1 ? t("sub.day") : t("sub.days")} ${t("side.leftInTrial")}`;

  const dismiss = () => {
    localStorage.setItem(KEY, today);
    setDismissed(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      data-testid="trial-ending-notice"
      className="relative mb-6 overflow-hidden rounded-2xl border border-primary/30 bg-primary/5 p-5"
    >
      <button onClick={dismiss} aria-label={t("trialend.later")} data-testid="trial-notice-dismiss"
              className="absolute right-3 top-3 text-muted-foreground transition-colors hover:text-foreground">
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Sparkles className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg">{t("trialend.heading")}</p>
          <p className="mt-0.5 text-xs font-medium text-primary">{daysLabel}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t("trialend.body")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full" onClick={() => navigate("/app/subscription")} data-testid="trial-notice-cta">
              {t("trialend.cta")}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={dismiss} data-testid="trial-notice-later">
              {t("trialend.later")}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
